import AppContext from 'App/AppContext';
import { IPPrimaryButton, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackTokensGapSmall, globalTextStylesBold } from 'globalStyles';
import { CopilotConversationItem } from 'models/copilot/copilotConversation';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ControllerExceptionCodes } from 'utils/controllerException';
import { signInToSkillControl } from './skillControlSignIn';

interface ISkillControlAccessCardProps {
  item: CopilotConversationItem;
}

/**
 * What the automation drafting conversation shows when SkillControl cannot be used yet (phase 22): the refusal's
 * sentence and what to do about it. A missing or expired sign-in (MCPCONN0004/0005) gets the one-click sign-in, a popup
 * at SkillControl; a SkillControl that is not available on this environment (MCPCONN0002), a missing role (MCPCONN0006)
 * or account (MCPCONN0007) is for an administrator, so the card says so and offers nothing to click.
 */
export const SkillControlAccessCard = (props: ISkillControlAccessCardProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['copilot']);
  const [isSigningIn, setIsSigningIn] = useState<boolean>(false);
  const [isSignedIn, setIsSignedIn] = useState<boolean>(false);

  const code = props.item.skillControlRefusal;
  const canSignIn =
    code === ControllerExceptionCodes.Code_McpConnection_UserNotAuthorized ||
    code === ControllerExceptionCodes.Code_McpConnection_NeedsReauthorization;

  const signIn = async () => {
    try {
      setIsSigningIn(true);
      const result = await signInToSkillControl(appContext);

      switch (result.outcome) {
        case 'connected':
          setIsSignedIn(true);
          appContext.showNotification(t('copilot:SkillControl.SignedIn'), false, false, true);
          // the refused execute goes again by itself: the server continues the run where it was (a row an exception
          // left Failed past the proposal is resumed, not restarted); without an action the user asks again
          if (props.item.action) await props.item.action();
          break;
        case 'blocked':
          appContext.showNotification(t('copilot:SkillControl.PopupBlocked'), true, false, true);
          break;
        case 'failed':
          appContext.showNotification(
            t('copilot:SkillControl.SignInFailed', { error: result.error ?? '' }),
            true,
            false,
            true,
          );
          break;
        default:
          // the person closed the popup: nothing to say, the card stays
          break;
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsSigningIn(false);
    }
  };

  return (
    <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { padding: 8 } }}>
      <IPStack.Item>
        <IPText block styles={globalTextStylesBold}>
          {t('copilot:SkillControl.Title')}
        </IPText>
      </IPStack.Item>
      <IPStack.Item>
        <IPText block>{props.item.response}</IPText>
      </IPStack.Item>
      {canSignIn && !isSignedIn && (
        <IPStack.Item>
          <IPPrimaryButton
            text={t('copilot:SkillControl.SignIn')}
            disabled={isSigningIn}
            onClick={() => {
              void signIn();
            }}
          />
        </IPStack.Item>
      )}
      {isSignedIn && !props.item.action && (
        <IPStack.Item>
          <IPText block variant="small">
            {t('copilot:SkillControl.AskAgain')}
          </IPText>
        </IPStack.Item>
      )}
    </IPStack>
  );
};
